import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
    TrendingUp,
    MousePointer,
    DollarSign,
    Percent,
    Calendar,
    ArrowUpRight,
    ArrowDownRight
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface Stats {
    totalClicks: number;
    totalConversions: number;
    totalEarnings: number;
    conversionRate: string;
    epc: string;
}

interface ChartData {
    date: string;
    clicks: number;
    conversions: number;
}

export default function AffiliateDashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState<Stats>({
        totalClicks: 0,
        totalConversions: 0,
        totalEarnings: 0,
        conversionRate: '0%',
        epc: '€0.00',
    });
    const [chartData, setChartData] = useState<ChartData[]>([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState('0');

    useEffect(() => {
        if (user?.uid) {
            fetchStats();
        }
    }, [user?.uid, dateRange]);

    const fetchStats = async () => {
        if (!user?.uid) return;

        setLoading(true);
        try {
            const days = parseInt(dateRange);
            const startDate = new Date();
            if (days === 0) {
                startDate.setHours(0, 0, 0, 0);
            } else {
                startDate.setDate(startDate.getDate() - days);
            }

            // Fetch clicks
            const clicksQuery = query(
                collection(db, 'clicks'),
                where('affiliate_id', '==', user.uid),
                where('timestamp', '>=', startDate),
                orderBy('timestamp', 'desc')
            );
            const clicksSnapshot = await getDocs(clicksQuery);
            const clicks = clicksSnapshot.docs.map(doc => doc.data());

            // Fetch conversions
            const conversionsQuery = query(
                collection(db, 'conversions'),
                where('affiliate_id', '==', user.uid),
                where('timestamp', '>=', startDate),
                orderBy('timestamp', 'desc')
            );
            const conversionsSnapshot = await getDocs(conversionsQuery);
            const conversions = conversionsSnapshot.docs.map(doc => doc.data());

            // Calculate stats
            const totalClicks = clicks.length;
            const totalConversions = conversions.length;
            const totalEarnings = conversions.reduce((sum, c) => sum + (c.commission_generated || 0), 0);
            const conversionRate = totalClicks > 0
                ? ((totalConversions / totalClicks) * 100).toFixed(2) + '%'
                : '0%';
            const epc = totalClicks > 0
                ? '€' + (totalEarnings / totalClicks).toFixed(2)
                : '€0.00';

            setStats({
                totalClicks,
                totalConversions,
                totalEarnings,
                conversionRate,
                epc,
            });

            // Prepare chart data (group by day)
            const chartMap = new Map<string, { clicks: number; conversions: number }>();

            // Initialize all dates
            const loopDays = days === 0 ? 1 : days;
            for (let i = 0; i < loopDays; i++) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateStr = date.toISOString().split('T')[0];
                chartMap.set(dateStr, { clicks: 0, conversions: 0 });
            }

            // Count clicks per day
            clicks.forEach(click => {
                const dateStr = click.timestamp.toDate().toISOString().split('T')[0];
                const existing = chartMap.get(dateStr) || { clicks: 0, conversions: 0 };
                chartMap.set(dateStr, { ...existing, clicks: existing.clicks + 1 });
            });

            // Count conversions per day
            conversions.forEach(conv => {
                const dateStr = conv.timestamp.toDate().toISOString().split('T')[0];
                const existing = chartMap.get(dateStr) || { clicks: 0, conversions: 0 };
                chartMap.set(dateStr, { ...existing, conversions: existing.conversions + 1 });
            });

            const chartDataArray = Array.from(chartMap.entries())
                .map(([date, data]) => ({
                    date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                    clicks: data.clicks,
                    conversions: data.conversions,
                }))
                .reverse();

            setChartData(chartDataArray);

        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const statCards = [
        {
            title: 'Total Clicks',
            value: stats.totalClicks.toLocaleString(),
            icon: MousePointer,
            color: 'from-blue-500 to-cyan-500',
            change: '+12%',
            positive: true,
        },
        {
            title: 'Conversions',
            value: stats.totalConversions.toLocaleString(),
            icon: TrendingUp,
            color: 'from-emerald-500 to-green-500',
            change: '+8%',
            positive: true,
        },
        {
            title: 'Earnings',
            value: '€' + stats.totalEarnings.toFixed(2),
            icon: DollarSign,
            color: 'from-primary-500 to-accent-500',
            change: '+15%',
            positive: true,
        },
        {
            title: 'EPC',
            value: stats.epc,
            icon: Percent,
            color: 'from-amber-500 to-orange-500',
            change: stats.conversionRate,
            positive: true,
        },
    ];

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Dashboard</h1>
                    <p className="text-gray-400">Welcome back, {user?.companyName || 'Affiliate'}</p>
                </div>

                <div className="flex items-center gap-2">
                    <Calendar size={20} className="text-gray-400" />
                    <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="input-field py-2 px-3 w-auto"
                    >
                        <option value="0">Today</option>
                        <option value="7">Last 7 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="90">Last 90 days</option>
                    </select>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <div
                            key={stat.title}
                            className="stat-card animate-slide-up"
                            style={{ animationDelay: `${index * 100}ms` }}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className={`w-12 h-12 rounded-xl bg-linear-to-br ${stat.color} flex items-center justify-center`}>
                                    <Icon className="text-white" size={24} />
                                </div>
                                <span className={`flex items-center gap-1 text-sm ${stat.positive ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {stat.positive ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                                    {stat.change}
                                </span>
                            </div>
                            <h3 className="text-gray-400 text-sm mb-1">{stat.title}</h3>
                            <p className="text-2xl font-bold text-white">{loading ? '...' : stat.value}</p>
                        </div>
                    );
                })}
            </div>

            {/* Chart */}
            <div className="card">
                <h2 className="text-lg font-semibold text-white mb-6">Performance Overview</h2>
                <div className="h-80">
                    {loading ? (
                        <div className="h-full flex items-center justify-center">
                            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis
                                    dataKey="date"
                                    stroke="#9ca3af"
                                    fontSize={12}
                                    tickLine={false}
                                />
                                <YAxis
                                    stroke="#9ca3af"
                                    fontSize={12}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'rgba(15, 15, 35, 0.9)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '12px',
                                        color: '#fff'
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="clicks"
                                    stroke="#6366f1"
                                    strokeWidth={2}
                                    dot={false}
                                    name="Clicks"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="conversions"
                                    stroke="#10b981"
                                    strokeWidth={2}
                                    dot={false}
                                    name="Conversions"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </div>
    );
}
